import Swal from 'sweetalert2';
import api from '../../utils/axios';
import { useState, useCallback } from 'react';

interface AreaItem {
  Id: number;
  Nombre: string;
  Ceco: string;
  Activo: boolean;
  Eliminado: boolean;
}

interface AreaDto {
  Nombre: string;
  Ceco: string;
  Activo: boolean;
}

interface SoftDeleteResponse {
  success: boolean;
  area: string;
  cargosAfectados?: {
    Id: number;
    Nombre: string;
  }[];
  message: string;
}

interface CheckCargosResponse {
  hasCargos: boolean;
  cargosActivos?: {
    Id: number;
    Nombre: string;
  }[];
}

const useAreasApi = () => {
  const [isLoading, setIsLoading] = useState(false);
  const baseUrl = '/administracion/areas';

  const getAll = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.post<AreaItem[]>(`${baseUrl}/findAll`);
      return response.data.filter(item => !item.Eliminado);
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const create = useCallback(async (data: AreaDto) => {
    setIsLoading(true);
    try {
      const response = await api.post(`${baseUrl}/create`, {
        ...data,
        Eliminado: false
      });
      return response.data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const checkCargosActivos = useCallback(async (id: number) => {
    try {
      const response = await api.get<CheckCargosResponse>(`${baseUrl}/check-cargos/${id}`);
      return response.data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }, []);

  const update = useCallback(async (id: number, data: AreaDto) => {
    setIsLoading(true);
    try {
      const response = await api.post(`${baseUrl}/update/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('API Error:', error);
      
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const remove = useCallback(async (id: number) => {
    setIsLoading(true);
    try {
      const response = await api.post<SoftDeleteResponse>(`${baseUrl}/softDelete/${id}`);
      
      if (response.data.cargosAfectados && response.data.cargosAfectados.length > 0) {
        // Primero mostrar los cargos y esperar confirmación
        const cargosList = response.data.cargosAfectados
          .map(cargo => `<li>${cargo.Nombre}</li>`)
          .join('');
  
        const confirmResult = await Swal.fire({
          title: 'Atención',
          html: `
            <p>Esta área tiene los siguientes cargos activos asociados:</p>
            <ul>${cargosList}</ul>
            <p>Si continúa, estos cargos serán desactivados.</p>
            <p>¿Desea continuar con la eliminación?</p>
          `,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Sí, eliminar',
          cancelButtonText: 'Cancelar'
        });
  
        if (!confirmResult.isConfirmed) {
          return false;
        }
  
        // Si el usuario confirma, procedemos con la eliminación real
        const deleteResponse = await api.post<SoftDeleteResponse>(`${baseUrl}/softDelete/${id}/confirm`);
  
        // Mostrar el resultado de la operación
        const cargosAfectadosList = deleteResponse.data.cargosAfectados
          ?.map(cargo => `<li>${cargo.Nombre}</li>`)
          .join('');
  
        await Swal.fire({
          title: 'Operación completada',
          html: `
            <p>Los siguientes cargos han sido desactivados:</p>
            <ul>${cargosAfectadosList}</ul>
            <p>${deleteResponse.data.message}</p>
          `,
          icon: 'success',
          confirmButtonText: 'OK'
        });
        
        return true;
      } else {
        // Si no hay cargos asociados, eliminar directamente
        await Swal.fire({
          title: 'Éxito',
          text: response.data.message,
          icon: 'success',
          confirmButtonText: 'OK'
        });
        return true;
      }
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    isLoading,
    getAll,
    create,
    update,
    remove,
    checkCargosActivos
  };
};

export { useAreasApi };
export type { AreaItem, AreaDto };